import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { Analytics } from "../../api";
import {
  CHART_AXIS_PROPS,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
  chartColor,
} from "../../lib/chart-colors";
import { fmtCompact, fmtInt, fmtMoney } from "../../lib/format";
import { InfoTooltip } from "../info-tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

type Measure = "revenue" | "count";

/**
 * Top xizmatlar — daromad yoki soni kesimida.
 *
 * DIQQAT: to'plamni server tanlaydi (`ORDER BY sum(Work.price) DESC LIMIT 5`),
 * ya'ni "Soni" tugmasi shu BESHTASINI qayta tartiblaydi, chinakam "soni
 * bo'yicha top-5" bermaydi. Shuning uchun sarlavha yonidagi ⓘ buni ochiq
 * aytadi — yangi endpoint qo'shmasdan halol yechim shu.
 */
export function TopServicesCard({ services }: { services: Analytics["services"] }) {
  const { t } = useTranslation("analytics");
  const [measure, setMeasure] = useState<Measure>("revenue");

  const data = useMemo(
    () =>
      [...services]
        .map((s) => ({ name: s.name, revenue: parseFloat(s.revenue), count: s.count }))
        .sort((a, b) => b[measure] - a[measure]),
    [services, measure],
  );

  const money = measure === "revenue";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">
            {t("topServices.title")}{" "}
            <InfoTooltip text={t("topServices.scopeHint")} />
          </CardTitle>
          {/* `InfoTooltip` TabsTrigger ICHIGA kirmaydi — u ham <button>. */}
          <Tabs value={measure} onValueChange={(v) => setMeasure(v as Measure)}>
            <TabsList>
              <TabsTrigger value="revenue">{t("topServices.byRevenue")}</TabsTrigger>
              <TabsTrigger value="count">{t("topServices.byCount")}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <EmptyState title={t("empty.noWorks")} className="py-8" />
        ) : (
          <div className="w-full h-[220px] sm:h-[260px]">
            <ResponsiveContainer>
              <BarChart data={data} layout="vertical" margin={{ left: 4, right: 8 }}>
                <CartesianGrid {...CHART_GRID_PROPS} horizontal={false} />
                <XAxis
                  type="number"
                  {...CHART_AXIS_PROPS}
                  tickFormatter={money ? fmtCompact : (v: number) => String(v)}
                  allowDecimals={!money ? false : undefined}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  {...CHART_AXIS_PROPS}
                  width={92}
                  tickFormatter={(v: string) => (v.length > 14 ? `${v.slice(0, 13)}…` : v)}
                />
                <Tooltip
                  {...CHART_TOOLTIP_PROPS}
                  formatter={(v: number) => (money ? fmtMoney(v) : fmtInt(v))}
                />
                <Bar
                  dataKey={measure}
                  name={money ? t("topServices.byRevenue") : t("topServices.byCount")}
                  fill={chartColor("chart-2")}
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
